import Decimal from 'decimal.js'

import {
  parseDecimal,
  type PnlTimeframe,
  resolveHeadlinePnl,
} from '@/features/dashboard/lib/account-summary-display'
import type { MarginModelFields } from '@/lib/margin-model'
import type { AccountSummary } from '@/types/generated/common/account'

/**
 * `AccountSummary` (types/generated/common/account.ts) has REQUIRED
 * `accountId`/`tradeVenue`/`timestamp` fields that have no single meaningful
 * value once summed across accounts/venues — this dedicated type covers only
 * the fields a caller actually needs to render the aggregate metrics block,
 * so `aggregateAccountSummaries` never has to fabricate placeholders for
 * those required-but-meaningless fields.
 *
 * Deliberately excludes pnl1dUsd/pnl1wUsd/pnl1mUsd/pnlAllUsd — those are
 * per-venue-gated (absent, not zero, until a venue's transfers gate is live)
 * and summing "only the accounts that happen to have the field populated"
 * would silently under-count. Headline timeframe PnL goes exclusively
 * through `aggregateHeadlinePnl` below, which resolves each account's own
 * best-available PnL source first, THEN sums.
 */
export interface AggregatedAccountSummary extends MarginModelFields {
  equityUsd: string
  grossNotionalUsd: string
  netNotionalUsd: string
  grossLeverage: string
  netLeverage: string
  unrealizedPnlUsd: string
  realizedPnlUsd: string
  dailyUnrealizedPnlUsd: string
}

function sumDecimal(values: (string | null | undefined)[]): Decimal {
  return values.reduce<Decimal>((total, value) => {
    const parsed = parseDecimal(value)
    return parsed === null ? total : total.plus(parsed)
  }, new Decimal(0))
}

/**
 * Sums the additive USD fields across accounts, then RECOMPUTES leverage from
 * the summed totals — grossLeverage/netLeverage are ratios, not additive, so
 * summing or averaging the per-account leverage strings directly would
 * produce a meaningless blended figure (e.g. 2x@$10k + 10x@$1k must NOT come
 * out to 6x; the correct blended figure is Σgross/Σequity). Returns `null`
 * when no scoped account has a summary yet.
 *
 * A summary whose `equityUsd` is empty/unparseable is the venue saying it can't
 * value that account right now. The aggregate equity is then "" (unknown) and so
 * is leverage — summing the other accounts would present a partial total as the
 * portfolio's, and leverage against it would overstate. Notional and PnL are
 * still summed: each account's own figures stay valid without its equity. Missing entries remain part of the
 * scope so non-additive fields cannot be attributed to a partially-loaded set.
 */
export function aggregateAccountSummaries(
  scopedSummaries: (AccountSummary | null | undefined)[]
): AggregatedAccountSummary | null {
  const summaries = scopedSummaries.filter((summary): summary is AccountSummary => summary != null)
  if (summaries.length === 0) return null

  const isEquityUnknown = summaries.some((s) => parseDecimal(s.equityUsd) === null)
  const equityUsd = sumDecimal(summaries.map((s) => s.equityUsd))
  const grossNotionalUsd = sumDecimal(summaries.map((s) => s.grossNotionalUsd))
  const netNotionalUsd = sumDecimal(summaries.map((s) => s.netNotionalUsd))
  const unrealizedPnlUsd = sumDecimal(summaries.map((s) => s.unrealizedPnlUsd))
  const realizedPnlUsd = sumDecimal(summaries.map((s) => s.realizedPnlUsd))
  const dailyUnrealizedPnlUsd = sumDecimal(summaries.map((s) => s.dailyUnrealizedPnlUsd))

  // The margin model is not additive and not blendable. It survives the
  // aggregate only when every scoped account agrees on all three fields —
  // the same rule the backend applies across an account's own currency rows,
  // for the same reason: half of a Cross-PM/Isolated-SM pair is not "the"
  // model, and showing one is worse than showing none.
  const margin =
    summaries.length === scopedSummaries.length &&
    summaries.every(
      (s) =>
        Boolean(s.marginModelLabel) &&
        s.marginModelLabel === summaries[0].marginModelLabel &&
        s.marginScope === summaries[0].marginScope &&
        s.marginMethod === summaries[0].marginMethod
    )
      ? {
          marginModelLabel: summaries[0].marginModelLabel,
          marginScope: summaries[0].marginScope,
          marginMethod: summaries[0].marginMethod,
        }
      : {}

  const isZeroEquity = equityUsd.isZero()
  const grossLeverage = isZeroEquity ? new Decimal(0) : grossNotionalUsd.div(equityUsd)
  const netLeverage = isZeroEquity ? new Decimal(0) : netNotionalUsd.abs().div(equityUsd)

  return {
    equityUsd: isEquityUnknown ? '' : equityUsd.toString(),
    grossNotionalUsd: grossNotionalUsd.toString(),
    netNotionalUsd: netNotionalUsd.toString(),
    grossLeverage: isEquityUnknown ? '' : grossLeverage.toString(),
    netLeverage: isEquityUnknown ? '' : netLeverage.toString(),
    unrealizedPnlUsd: unrealizedPnlUsd.toString(),
    realizedPnlUsd: realizedPnlUsd.toString(),
    dailyUnrealizedPnlUsd: dailyUnrealizedPnlUsd.toString(),
    ...margin,
  }
}

/**
 * Headline timeframe PnL across every scoped account. Each account resolves
 * its OWN best-available PnL source first (server field for the window, else
 * its own fallback — see `resolveHeadlinePnl`/`resolvePnlFallback`), THEN the
 * results are summed — never sum-then-resolve, or an account whose venue
 * lacks the server field for this timeframe would silently contribute 0
 * instead of its own fallback estimate.
 */
export function aggregateHeadlinePnl(
  timeframe: PnlTimeframe,
  entries: { summary: AccountSummary | null | undefined; fallbackPnl: number }[]
): number {
  return entries.reduce(
    (total, entry) => total + resolveHeadlinePnl(timeframe, entry.summary, entry.fallbackPnl),
    0
  )
}
